import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { PortalUserRecord } from "@portal/schemas";
import {
  Badge,
  Button,
  Card,
  CardAction,
  CardContent,
  CardHeader,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@portal/ui";
import { Archive, ArchiveRestore, Eye, EyeOff, MoreVertical, Pencil, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { setPortalUserArchived } from "../../api/client";
import { USERS_KEY, useUsers } from "../../api/queries";
import { InlineError } from "../../components/InlineError";
import { CardStatus } from "../../components/cards/CardStatus";
import { DataTable, createDataTableColumns } from "../../components/DataTable";
import { CardTitleWithHint } from "../../components/Hint";
import { UserNameDialog } from "../users/UserNameDialog";

const dateTime = new Intl.DateTimeFormat("ja-JP", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Asia/Tokyo",
});
const col = createDataTableColumns<PortalUserRecord>();

/**
 * ユーザーマスタ(SPEC §2。ユーザー決定 2026-10-15)。全体の管理者は、誰の名前でも登録・変更できる(本人も自分の名前は変えられる)。
 * 初めてログインした人は自動で載る(名前は未設定)。まだログインしていない人は、メールで先に登録できる。
 * 一覧は、名前が未設定の人が先(登録を忘れないように)、そのあとはよみがなの順。
 */
export function AdminUsersPage() {
  const users = useUsers();
  const [editing, setEditing] = useState<PortalUserRecord | null>(null);
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const queryClient = useQueryClient();
  const archive = useMutation({
    mutationFn: (u: PortalUserRecord) =>
      setPortalUserArchived(u.email, { archived: !u.archived, revision: u.revision }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_KEY }),
  });
  const columns = useMemo(
    () =>
      col.columns([
        col.accessor((u) => (u.nameRegistered ? u.displayName : ""), {
          id: "name",
          header: "名前",
          cell: (info) => (
            <span className="flex items-center gap-2">
              {info.row.original.nameRegistered ? (
                info.getValue()
              ) : (
                <Badge variant="warning">未設定</Badge>
              )}
              {info.row.original.archived && <Badge variant="outline">アーカイブ</Badge>}
            </span>
          ),
        }),
        col.accessor((u) => (u.nameRegistered ? `${u.familyNameKana} ${u.givenNameKana}` : ""), {
          id: "kana",
          header: "よみがな",
        }),
        col.accessor("email", { header: "メール" }),
        col.accessor((u) => (u.lastLoginAt ? dateTime.format(new Date(u.lastLoginAt)) : "—"), {
          id: "lastLoginAt",
          header: "最終ログイン",
          meta: { className: "whitespace-nowrap" },
        }),
        col.display({
          id: "actions",
          header: "",
          // 一覧の行の操作は「⋮」のメニューにまとめる(ユーザー要望 2026-10-15)
          cell: (info) => {
            const u = info.row.original;
            return (
              <div className="flex justify-end">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`${u.email} のメニュー`}
                      title="メニュー"
                    >
                      <MoreVertical />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-40">
                    <DropdownMenuItem onSelect={() => setEditing(u)}>
                      <Pencil />
                      名前を編集
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={archive.isPending}
                      onSelect={() => archive.mutate(u)}
                    >
                      {u.archived ? <ArchiveRestore /> : <Archive />}
                      {u.archived ? "元に戻す" : "アーカイブ"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          },
        }),
      ]),
    [archive],
  );
  const rows = (users.data?.users ?? []).filter((u) => showArchived || !u.archived);
  return (
    <Card data-testid="admin-users-card">
      <CardHeader>
        <CardTitleWithHint hint="名前は本人も自分で設定できます(アカウントメニューの「名前を設定」)。初めてログインした人は自動で載ります(名前は未設定)。まだログインしていない人も、メールで先に登録できます。">
          ユーザー
        </CardTitleWithHint>
        <CardAction className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? <EyeOff /> : <Eye />}
            {showArchived ? "アーカイブを隠す" : "アーカイブも表示"}
          </Button>
          <Button onClick={() => setCreating(true)}>
            <Plus />
            ユーザーを追加
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <CardStatus
          isPending={users.isPending}
          isError={users.isError}
          refetch={() => users.refetch()}
        />
        <InlineError error={archive.error} />
        {users.data && (
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(u) => u.email}
            emptyText="ユーザーはいません。"
            searchPlaceholder="名前・よみがな・メールで検索"
          />
        )}
      </CardContent>
      <UserNameDialog
        key={editing?.email ?? (creating ? "new" : "none")}
        target={editing}
        open={creating || editing !== null}
        onOpenChange={(open) => {
          if (open) return;
          setCreating(false);
          setEditing(null);
        }}
      />
    </Card>
  );
}
